import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const SLIDES = [
  {
    image:    "/images/image1.jpg",
    tag:      "Handcrafted Soy Wax Collection",
    heading:  "Ignite Your",
    italic:   "Senses",
    body:     "Experience the warmth of our sustainable soy wax collection. Each scent is a curated journey designed to transform your space.",
    overlay:  "linear-gradient(105deg, rgba(15,8,0,0.80) 0%, rgba(35,18,0,0.60) 45%, rgba(0,0,0,0.15) 100%)",
    fallback: "linear-gradient(120deg, #1a0f00 0%, #2e1800 35%, #3d2200 55%, #5a3800 75%, #c49a5a 100%)",
  },
  {
    image:    "/images/image2.jpg",
    tag:      "Premium Fragrance Oils",
    heading:  "Scents That",
    italic:   "Linger",
    body:     "Every candle is hand-poured in small batches in our Mumbai studio using 100% natural botanicals and fragrance oils.",
    overlay:  "linear-gradient(105deg, rgba(8,16,8,0.80) 0%, rgba(15,30,15,0.60) 45%, rgba(0,0,0,0.15) 100%)",
    fallback: "linear-gradient(120deg, #0d1a0f 0%, #1a3020 40%, #2a4a30 65%, #4a7a50 100%)",
  },
  {
    image:    "/images/image3.jpg",
    tag:      "Perfect for Every Occasion",
    heading:  "Gifts They'll",
    italic:   "Treasure",
    body:     "Beautifully packaged in zero-waste seed paper — the perfect gift for birthdays, anniversaries, and every celebration.",
    overlay:  "linear-gradient(105deg, rgba(16,8,16,0.80) 0%, rgba(30,10,35,0.60) 45%, rgba(0,0,0,0.15) 100%)",
    fallback: "linear-gradient(120deg, #1a0d1a 0%, #2e1030 40%, #4a1a50 65%, #9a5aaa 100%)",
  },
  {
    image:    "/images/image4.jpg",
    tag:      "Customise Your Candle",
    heading:  "Your Story,",
    italic:   "Your Scent",
    body:     "Choose your fragrance, size and label. We'll handcraft your personalised candle and deliver it to your door.",
    overlay:  "linear-gradient(105deg, rgba(18,14,0,0.80) 0%, rgba(40,28,0,0.60) 45%, rgba(0,0,0,0.15) 100%)",
    fallback: "linear-gradient(120deg, #1a1200 0%, #2e2000 40%, #503800 65%, #aa8020 100%)",
  },
];

const AUTO_INTERVAL = 3000;  // 3 seconds — medium

export default function HeroSection() {
  const navigate = useNavigate();
  const [current, setCurrent]     = useState(0);
  const [prev, setPrev]           = useState(null);
  const [direction, setDirection] = useState("next");
  const [imgErrors, setImgErrors] = useState({});

  const goTo = useCallback((index, dir = "next") => {
    if (index === current) return;
    setPrev(current);
    setDirection(dir);
    setCurrent(index);
    setTimeout(() => setPrev(null), 900);
  }, [current]);

  const goNext = useCallback(() => {
    goTo((current + 1) % SLIDES.length, "next");
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + SLIDES.length) % SLIDES.length, "prev");
  }, [current, goTo]);

  // Auto-advance
  useEffect(() => {
    const t = setTimeout(goNext, AUTO_INTERVAL);
    return () => clearTimeout(t);
  }, [current, goNext]);

  const scrollToCategories = () =>
    document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" });

  const scrollToCustomize = () =>
    document.getElementById("customize")?.scrollIntoView({ behavior: "smooth" });

  const slide = SLIDES[current];

  return (
    <section style={{ position: "relative", width: "100%", height: "100vh",
      overflow: "hidden", boxSizing: "border-box" }}>

      {/* ── Slide layers ── */}
      {SLIDES.map((s, i) => {
        const isActive = i === current;
        const isPrev   = i === prev;
        return (
          <div key={i} style={{
            position: "absolute", inset: 0, zIndex: isActive ? 2 : isPrev ? 1 : 0,
            opacity: isActive ? 1 : isPrev ? 0 : 0,
            transform: isActive
              ? "scale(1)"
              : isPrev
                ? `scale(1.04) translateX(${direction === "next" ? "-3%" : "3%"})`
                : "scale(1)",
            transition: "opacity 0.85s ease, transform 0.85s ease",
          }}>
            {/* Background image */}
            {!imgErrors[i] ? (
              <img
                src={s.image}
                alt=""
                onError={() => setImgErrors(e => ({ ...e, [i]: true }))}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%",
                  objectFit: "cover", objectPosition: "center" }}
              />
            ) : (
              <div style={{ position: "absolute", inset: 0, background: s.fallback }} />
            )}
            {/* Colour overlay */}
            <div style={{ position: "absolute", inset: 0, background: s.overlay }} />
          </div>
        );
      })}

      {/* ── Glow orbs ── */}
      <div style={{ position: "absolute", right: "12%", top: "8%", zIndex: 3,
        width: "340px", height: "440px", borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(ellipse, rgba(230,160,60,0.40) 0%, rgba(200,120,30,0.18) 30%, transparent 70%)",
        filter: "blur(40px)", transition: "opacity 0.85s ease" }} />

      {/* ── Text content ── */}
      <div style={{ position: "relative", zIndex: 10, height: "100%", display: "flex",
        flexDirection: "column", justifyContent: "center",
        paddingTop: "64px", paddingLeft: "7%", paddingRight: "7%",
        boxSizing: "border-box" }}>

        {/* Tag */}
        <p key={`tag-${current}`} style={{ color: "#e0a060", fontFamily: "Georgia, serif",
          fontSize: "11px", letterSpacing: "0.22em", textTransform: "uppercase",
          marginBottom: "18px", animation: "heroFadeUp 0.7s ease both" }}>
          {slide.tag}
        </p>

        {/* Heading */}
        <h1 key={`h1-${current}`} style={{ fontFamily: "Georgia, serif",
          fontSize: "clamp(38px, 5.5vw, 78px)", fontWeight: "700",
          lineHeight: "1.08", color: "#f5ede0", marginBottom: "22px",
          maxWidth: "600px", animation: "heroFadeUp 0.8s ease 0.08s both" }}>
          {slide.heading}<br />
          <em style={{ color: "#e8a855", fontStyle: "italic" }}>{slide.italic}</em>
        </h1>

        {/* Body */}
        <p key={`body-${current}`} style={{ fontFamily: "Georgia, serif",
          fontSize: "clamp(13px, 1.2vw, 15px)", color: "rgba(245,237,224,0.72)",
          maxWidth: "380px", lineHeight: "1.8", marginBottom: "42px",
          animation: "heroFadeUp 0.9s ease 0.16s both" }}>
          {slide.body}
        </p>

        {/* Buttons */}
        <div key={`btns-${current}`} style={{ display: "flex", flexWrap: "wrap",
          gap: "16px", animation: "heroFadeUp 1s ease 0.24s both" }}>
          <button onClick={() => navigate("/products")}
            style={{ padding: "14px 30px", background: "#e07b39", color: "#fff",
              border: "none", cursor: "pointer", borderRadius: "999px",
              fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600",
              letterSpacing: "0.1em", textTransform: "uppercase",
              boxShadow: "0 6px 24px rgba(224,123,57,0.45)", transition: "all 0.25s" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#c96a28"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#e07b39"; e.currentTarget.style.transform = "translateY(0)"; }}>
            Shop Signature Scents
          </button>

          <button onClick={scrollToCategories}
            style={{ padding: "14px 30px", background: "transparent", color: "#f5ede0",
              border: "1.5px solid rgba(245,237,224,0.45)", cursor: "pointer",
              borderRadius: "999px", fontFamily: "Georgia, serif", fontSize: "12px",
              fontWeight: "600", letterSpacing: "0.1em", textTransform: "uppercase",
              transition: "all 0.25s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(245,237,224,0.9)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(245,237,224,0.45)"; e.currentTarget.style.transform = "translateY(0)"; }}>
            Discover More
          </button>

          <button onClick={scrollToCustomize}
            style={{ padding: "14px 30px", background: "transparent", color: "#f5ede0",
              border: "1.5px solid rgba(245,237,224,0.45)", cursor: "pointer",
              borderRadius: "999px", fontFamily: "Georgia, serif", fontSize: "12px",
              fontWeight: "600", letterSpacing: "0.1em", textTransform: "uppercase",
              transition: "all 0.25s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(245,237,224,0.9)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(245,237,224,0.45)"; e.currentTarget.style.transform = "translateY(0)"; }}>
            Customize
          </button>
        </div>
      </div>

      {/* ── Prev / Next arrows ── */}
      <button onClick={goPrev}
        style={{ position: "absolute", left: "24px", top: "50%",
          transform: "translateY(-50%)", zIndex: 20,
          width: "44px", height: "44px", borderRadius: "50%",
          background: "rgba(255,255,255,0.12)", border: "1.5px solid rgba(255,255,255,0.25)",
          color: "#fff", fontSize: "18px", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(6px)", transition: "background 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}>
        ‹
      </button>

      <button onClick={goNext}
        style={{ position: "absolute", right: "24px", top: "50%",
          transform: "translateY(-50%)", zIndex: 20,
          width: "44px", height: "44px", borderRadius: "50%",
          background: "rgba(255,255,255,0.12)", border: "1.5px solid rgba(255,255,255,0.25)",
          color: "#fff", fontSize: "18px", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          backdropFilter: "blur(6px)", transition: "background 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}>
        ›
      </button>

      {/* ── Dot indicators ── */}
      <div style={{ position: "absolute", bottom: "36px", left: "50%",
        transform: "translateX(-50%)", zIndex: 20,
        display: "flex", gap: "10px", alignItems: "center" }}>
        {SLIDES.map((_, i) => (
          <button key={i} onClick={() => goTo(i, i > current ? "next" : "prev")}
            style={{ width: i === current ? "28px" : "8px", height: "8px",
              borderRadius: "999px", border: "none", cursor: "pointer",
              background: i === current ? "#e07b39" : "rgba(255,255,255,0.45)",
              padding: 0, transition: "all 0.35s ease" }} />
        ))}
      </div>

      {/* ── Slide counter ── */}
      <div style={{ position: "absolute", bottom: "36px", right: "40px", zIndex: 20 }}>
        <span style={{ fontFamily: "Georgia, serif", fontSize: "12px",
          color: "rgba(255,255,255,0.5)" }}>
          {String(current + 1).padStart(2, "0")}
          <span style={{ margin: "0 6px", color: "rgba(255,255,255,0.25)" }}>/</span>
          {String(SLIDES.length).padStart(2, "0")}
        </span>
      </div>

      {/* ── Progress bar ── */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0,
        height: "3px", background: "rgba(255,255,255,0.12)", zIndex: 20 }}>
        <div key={current} style={{ height: "100%", background: "#e07b39",
          animation: `progressBar ${AUTO_INTERVAL}ms linear both` }} />
      </div>

      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(26px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes progressBar {
          from { width: 0%; }
          to   { width: 100%; }
        }
      `}</style>
    </section>
  );
}